/**
 * Hand-drawn HUD numerals + icon set.
 *
 * Every number the HUD shows (ammo, distances, score, timer, heading) is drawn
 * from these path definitions rather than a font, so the look is identical on
 * every machine and needs no webfont. The digits are tabular (fixed advance) so
 * a counter never jitters as it changes.
 *
 * Coordinates are stroke CENTRELINES in a normalised [0,1]x[0,1] box, y down.
 * The chamfer constants bake in a ~1.72 aspect so the corner cuts read as equal
 * length in both axes at the sizes the HUD actually uses.
 */
import { COL, FONT, rgba } from './Theme.js';

const CX = 0.26; // horizontal chamfer, normalised
const CY = 0.15; // vertical chamfer, normalised

/** sub-path: { p: [[x,y]...], c?: closed } */
const GLYPHS = {
  '0': { adv: 0.58, s: [{ p: [[0, CY], [CX, 0], [1 - CX, 0], [1, CY], [1, 1 - CY], [1 - CX, 1], [CX, 1], [0, 1 - CY]], c: true }] },
  '1': { adv: 0.58, s: [{ p: [[0.08, 0.23], [0.52, 0], [0.52, 1]] }] },
  '2': { adv: 0.58, s: [{ p: [[0, 0.22], [CX, 0], [1 - CX, 0], [1, 0.20], [1, 0.40], [0.03, 1], [1, 1]] }] },
  '3': {
    adv: 0.58,
    s: [
      { p: [[0, 0.18], [CX, 0], [1 - CX, 0], [1, 0.18], [1, 0.36], [0.78, 0.5], [1, 0.64], [1, 0.82], [1 - CX, 1], [CX, 1], [0, 0.82]] },
      { p: [[0.32, 0.5], [0.78, 0.5]] },
    ],
  },
  '4': { adv: 0.58, s: [{ p: [[0.74, 0], [0, 0.71], [1, 0.71]] }, { p: [[0.74, 0], [0.74, 1]] }] },
  '5': { adv: 0.58, s: [{ p: [[1, 0], [0.05, 0], [0, 0.42], [0.70, 0.37], [1, 0.56], [1, 0.82], [1 - CX, 1], [CX, 1], [0, 0.82]] }] },
  '6': { adv: 0.58, s: [{ p: [[1, 0.16], [1 - CX, 0], [CX, 0], [0, 0.18], [0, 0.82], [CX, 1], [1 - CX, 1], [1, 0.82], [1, 0.64], [1 - CX, 0.50], [CX, 0.50], [0, 0.62]] }] },
  '7': { adv: 0.58, s: [{ p: [[0, 0], [1, 0], [1, 0.11], [0.29, 1]] }] },
  '8': {
    adv: 0.58,
    s: [{ p: [[0, 0.16], [CX, 0], [1 - CX, 0], [1, 0.16], [1, 0.34], [1 - CX, 0.5], [1, 0.66], [1, 0.84], [1 - CX, 1], [CX, 1], [0, 0.84], [0, 0.66], [CX, 0.5], [0, 0.34]], c: true }],
  },
  '9': { adv: 0.58, s: [{ p: [[0, 0.84], [CX, 1], [1 - CX, 1], [1, 0.82], [1, 0.18], [1 - CX, 0], [CX, 0], [0, 0.18], [0, 0.36], [CX, 0.50], [1 - CX, 0.50], [1, 0.38]] }] },
  '/': { adv: 0.42, s: [{ p: [[1, -0.04], [0, 1.04]] }] },
  '-': { adv: 0.44, s: [{ p: [[0, 0.52], [1, 0.52]] }] },
  '+': { adv: 0.50, s: [{ p: [[0, 0.52], [1, 0.52]] }, { p: [[0.5, 0.22], [0.5, 0.82]] }] },
  ':': { adv: 0.26, s: [], dots: [0.25, 0.75] },
  '.': { adv: 0.26, s: [], dots: [0.99] },
  ' ': { adv: 0.34, s: [] },
  '%': { adv: 0.70, s: [{ p: [[0.95, 0.02], [0.05, 0.98]] }], dots: [], pct: true },
  'M': { adv: 0.72, s: [{ p: [[0, 1], [0, 0], [0.5, 0.52], [1, 0], [1, 1]] }] },
};

export function glyphAdvance(ch, h) {
  const g = GLYPHS[ch] || GLYPHS[' '];
  return g.adv * h;
}

export function measureGlyphs(text, h, tracking = 0.08) {
  let w = 0;
  for (let i = 0; i < text.length; i++) {
    w += glyphAdvance(text[i], h);
    if (i < text.length - 1) w += tracking * h;
  }
  return w;
}

/**
 * Draw a hand-built numeric string.
 * opt: { color, weight (stroke width as fraction of h), tracking, align,
 *        halo (dark outline colour|null), haloWidth, alpha, italic }
 */
export function drawGlyphs(g, text, x, y, h, opt = {}) {
  const weight = opt.weight ?? 0.155;
  const tracking = opt.tracking ?? 0.08;
  const sw = h * weight;
  const align = opt.align || 'left';
  const total = measureGlyphs(text, h, tracking);
  let ox = x;
  if (align === 'right') ox = x - total;
  else if (align === 'center') ox = x - total / 2;

  const skew = opt.italic ? -0.10 : 0;

  g.save();
  if (opt.alpha != null) g.globalAlpha *= opt.alpha;
  g.lineJoin = 'miter';
  g.miterLimit = 2.4;
  g.lineCap = 'butt';

  const halo = opt.halo === undefined ? COL.shadow : opt.halo;
  const hw = opt.haloWidth ?? Math.max(1.4, h * 0.045);

  // Two passes: dark halo underneath, then the glyph. Keeps the HUD legible
  // against a blown-out sky without a blurry drop shadow.
  for (let pass = halo ? 0 : 1; pass < 2; pass++) {
    g.strokeStyle = pass === 0 ? halo : (opt.color || COL.white);
    g.fillStyle = g.strokeStyle;
    g.lineWidth = pass === 0 ? sw + hw * 2 : sw;
    let cx = ox;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const def = GLYPHS[ch] || GLYPHS[' '];
      const adv = def.adv * h;
      const bw = adv - sw;
      const bh = h - sw;
      const X = (nx, ny) => cx + sw / 2 + nx * bw + skew * (1 - ny) * h * 0.5;
      const Y = (ny) => y + sw / 2 + ny * bh;
      for (const sub of def.s) {
        g.beginPath();
        for (let k = 0; k < sub.p.length; k++) {
          const [nx, ny] = sub.p[k];
          const px = X(nx, ny), py = Y(ny);
          if (k === 0) g.moveTo(px, py); else g.lineTo(px, py);
        }
        if (sub.c) g.closePath();
        g.stroke();
      }
      if (def.dots) {
        for (const dy of def.dots) {
          const d = sw * (pass === 0 ? 1 + (hw * 2) / sw : 1);
          g.fillRect(X(0.5, dy) - d / 2, Y(dy) - d / 2, d, d);
        }
      }
      if (def.pct) {
        const r = bh * 0.17;
        const d = pass === 0 ? r + hw : r;
        g.lineWidth = pass === 0 ? hw * 2 : sw * 0.8;
        for (const [nx, ny] of [[0.20, 0.18], [0.80, 0.82]]) {
          g.beginPath();
          g.arc(X(nx, ny), Y(ny), d, 0, Math.PI * 2);
          g.stroke();
        }
        g.lineWidth = pass === 0 ? sw + hw * 2 : sw;
      }
      cx += adv + tracking * h;
    }
  }
  g.restore();
  return total;
}

/* ----------------------------------------------------------------- labels */

const HAS_TRACKING = (() => {
  try {
    const c = document.createElement('canvas').getContext('2d');
    return 'letterSpacing' in c;
  } catch { return false; }
})();

/**
 * Small uppercase label in the system stack, with the same dark halo treatment
 * so labels and numerals sit at the same optical weight.
 * opt: { color, size, weight, tracking(em), align, halo, alpha, baseline }
 */
export function label(g, text, x, y, opt = {}) {
  const size = opt.size ?? 12;
  const w = opt.weight ?? 600;
  g.save();
  if (opt.alpha != null) g.globalAlpha *= opt.alpha;
  g.font = `${w} ${size}px ${FONT}`;
  if (HAS_TRACKING) g.letterSpacing = `${(opt.tracking ?? 0.14) * size}px`;
  g.textAlign = opt.align || 'left';
  g.textBaseline = opt.baseline || 'top';
  const halo = opt.halo === undefined ? COL.shadow : opt.halo;
  if (halo) {
    g.lineJoin = 'round';
    g.miterLimit = 2;
    g.lineWidth = Math.max(2, size * 0.24);
    g.strokeStyle = halo;
    g.strokeText(text, x, y);
  }
  g.fillStyle = opt.color || COL.white;
  g.fillText(text, x, y);
  const m = g.measureText(text).width;
  g.restore();
  return m;
}

export function measureLabel(g, text, opt = {}) {
  const size = opt.size ?? 12;
  g.save();
  g.font = `${opt.weight ?? 600} ${size}px ${FONT}`;
  if (HAS_TRACKING) g.letterSpacing = `${(opt.tracking ?? 0.14) * size}px`;
  const m = g.measureText(text).width;
  g.restore();
  return m;
}

/* ------------------------------------------------------------------ icons */

function strokePath(g, pts, close) {
  g.beginPath();
  for (let i = 0; i < pts.length; i += 2) {
    if (i === 0) g.moveTo(pts[0], pts[1]); else g.lineTo(pts[i], pts[i + 1]);
  }
  if (close) g.closePath();
}

/** Icons draw inside a w x h box anchored at (x,y). All are stroke-based so the
 *  halo pass is identical to the glyphs. */
export const ICON = {
  /** Assault rifle silhouette, used in the killfeed. */
  rifle(g, x, y, w, h) {
    const s = w / 40;
    g.beginPath();
    g.moveTo(x + 2 * s, y + h * 0.42); g.lineTo(x + 26 * s, y + h * 0.42);
    g.lineTo(x + 26 * s, y + h * 0.28); g.lineTo(x + 38 * s, y + h * 0.28);
    g.lineTo(x + 38 * s, y + h * 0.44); g.lineTo(x + 29 * s, y + h * 0.5);
    g.lineTo(x + 20 * s, y + h * 0.5); g.lineTo(x + 17 * s, y + h * 0.86);
    g.lineTo(x + 12 * s, y + h * 0.86); g.lineTo(x + 13 * s, y + h * 0.5);
    g.lineTo(x + 2 * s, y + h * 0.5); g.closePath();
    g.fill();
    g.fillRect(x + 8 * s, y + h * 0.5, 3 * s, h * 0.34);
    g.fillRect(x + 20 * s, y + h * 0.16, 3 * s, h * 0.26);
  },
  smg(g, x, y, w, h) {
    const s = w / 40;
    g.beginPath();
    g.moveTo(x + 4 * s, y + h * 0.40); g.lineTo(x + 30 * s, y + h * 0.40);
    g.lineTo(x + 30 * s, y + h * 0.30); g.lineTo(x + 38 * s, y + h * 0.30);
    g.lineTo(x + 38 * s, y + h * 0.48); g.lineTo(x + 22 * s, y + h * 0.52);
    g.lineTo(x + 19 * s, y + h * 0.84); g.lineTo(x + 14 * s, y + h * 0.84);
    g.lineTo(x + 15 * s, y + h * 0.52); g.lineTo(x + 4 * s, y + h * 0.52);
    g.closePath(); g.fill();
    g.fillRect(x + 22 * s, y + h * 0.52, 4 * s, h * 0.40);
  },
  sniper(g, x, y, w, h) {
    const s = w / 40;
    g.fillRect(x + 2 * s, y + h * 0.44, 36 * s, h * 0.10);
    g.fillRect(x + 16 * s, y + h * 0.24, 12 * s, h * 0.12);
    g.beginPath();
    g.moveTo(x + 10 * s, y + h * 0.54); g.lineTo(x + 16 * s, y + h * 0.54);
    g.lineTo(x + 14 * s, y + h * 0.90); g.lineTo(x + 8 * s, y + h * 0.90);
    g.closePath(); g.fill();
  },
  pistol(g, x, y, w, h) {
    const s = w / 40;
    g.beginPath();
    g.moveTo(x + 10 * s, y + h * 0.34); g.lineTo(x + 34 * s, y + h * 0.34);
    g.lineTo(x + 34 * s, y + h * 0.50); g.lineTo(x + 22 * s, y + h * 0.50);
    g.lineTo(x + 18 * s, y + h * 0.88); g.lineTo(x + 10 * s, y + h * 0.88);
    g.closePath(); g.fill();
  },
  knife(g, x, y, w, h) {
    const s = w / 40;
    g.beginPath();
    g.moveTo(x + 4 * s, y + h * 0.72); g.lineTo(x + 26 * s, y + h * 0.24);
    g.lineTo(x + 30 * s, y + h * 0.34); g.lineTo(x + 10 * s, y + h * 0.80);
    g.closePath(); g.fill();
    g.fillRect(x + 28 * s, y + h * 0.20, 9 * s, h * 0.12);
  },
  grenade(g, x, y, w, h) {
    const cx = x + w / 2, cy = y + h * 0.60, r = Math.min(w, h) * 0.30;
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill();
    g.fillRect(cx - r * 0.42, y + h * 0.14, r * 0.84, h * 0.20);
    g.fillRect(cx + r * 0.3, y + h * 0.06, r * 0.8, h * 0.09);
  },
  stun(g, x, y, w, h) {
    const cx = x + w / 2, cy = y + h * 0.55, r = Math.min(w, h) * 0.30;
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill();
    g.fillRect(cx - r * 0.34, y + h * 0.12, r * 0.68, h * 0.22);
    g.save(); g.globalCompositeOperation = 'destination-out';
    strokePath(g, [cx - r * 0.1, cy - r * 0.5, cx + r * 0.4, cy - r * 0.5, cx + r * 0.05, cy + r * 0.05, cx + r * 0.45, cy + r * 0.05, cx - r * 0.15, cy + r * 0.6, cx + r * 0.1, cy + r * 0.05, cx - r * 0.3, cy + r * 0.05], true);
    g.fill(); g.restore();
  },
  /** STANAG magazine — reads far better than a rifle at 24 px. */
  magazine(g, x, y, w, h) {
    const bw = w * 0.74, bx = x + (w - bw) / 2;
    g.beginPath();
    g.moveTo(bx + bw * 0.06, y);
    g.lineTo(bx + bw * 0.94, y);
    g.lineTo(bx + bw, y + h * 0.10);
    g.lineTo(bx + bw * 0.88, y + h);
    g.lineTo(bx + bw * 0.18, y + h);
    g.lineTo(bx, y + h * 0.10);
    g.closePath();
    g.fill();
    g.save();
    g.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 3; i++) {
      g.fillRect(bx + bw * 0.20, y + h * (0.30 + i * 0.20), bw * 0.60, h * 0.075);
    }
    g.restore();
  },
  /** Small skull used for headshot kills in the killfeed. */
  skull(g, x, y, w, h) {
    const cx = x + w / 2, cy = y + h * 0.44, r = Math.min(w, h) * 0.36;
    g.beginPath();
    g.moveTo(cx - r, cy + r * 0.15);
    g.quadraticCurveTo(cx - r, cy - r * 1.05, cx, cy - r * 1.05);
    g.quadraticCurveTo(cx + r, cy - r * 1.05, cx + r, cy + r * 0.15);
    g.lineTo(cx + r * 0.55, cy + r * 0.55);
    g.lineTo(cx + r * 0.5, cy + r * 1.0);
    g.lineTo(cx - r * 0.5, cy + r * 1.0);
    g.lineTo(cx - r * 0.55, cy + r * 0.55);
    g.closePath();
    g.fill();
    g.save();
    g.globalCompositeOperation = 'destination-out';
    g.beginPath(); g.ellipse(cx - r * 0.42, cy - r * 0.15, r * 0.28, r * 0.32, 0, 0, Math.PI * 2); g.fill();
    g.beginPath(); g.ellipse(cx + r * 0.42, cy - r * 0.15, r * 0.28, r * 0.32, 0, 0, Math.PI * 2); g.fill();
    g.fillRect(cx - r * 0.10, cy + r * 0.55, r * 0.20, r * 0.45);
    g.restore();
  },
  /** Stance pip trio (stand / crouch / prone) — index picks the filled one. */
  stance(g, x, y, w, h, mode) {
    const bw = w * 0.30;
    for (let i = 0; i < 3; i++) {
      const on = i === mode;
      g.globalAlpha = on ? 1 : 0.26;
      const bh = h * [1, 0.66, 0.34][i];
      g.fillRect(x + i * (bw + w * 0.05), y + h - bh, bw, bh);
    }
    g.globalAlpha = 1;
  },
  chevron(g, cx, cy, s, dir = 0) {
    g.save(); g.translate(cx, cy); g.rotate(dir);
    g.beginPath();
    g.moveTo(0, -s); g.lineTo(s * 0.82, s * 0.62); g.lineTo(0, s * 0.24); g.lineTo(-s * 0.82, s * 0.62);
    g.closePath(); g.fill(); g.restore();
  },
  diamond(g, cx, cy, s) {
    g.beginPath();
    g.moveTo(cx, cy - s); g.lineTo(cx + s, cy); g.lineTo(cx, cy + s); g.lineTo(cx - s, cy);
    g.closePath();
  },
};

export const WEAPON_ICON = {
  rifle: ICON.rifle, ar: ICON.rifle, smg: ICON.smg, sniper: ICON.sniper,
  pistol: ICON.pistol, knife: ICON.knife, melee: ICON.knife, grenade: ICON.grenade,
};

/** Draw an icon with the standard dark halo, in a single call. */
export function icon(name, g, x, y, w, h, color = COL.white, extra) {
  const fn = typeof name === 'function' ? name : (ICON[name] || WEAPON_ICON[name]);
  if (!fn) return;
  g.save();
  g.shadowColor = rgba('#000000', 0.75);
  g.shadowBlur = 0;
  g.shadowOffsetX = 0;
  g.shadowOffsetY = 1;
  g.fillStyle = color;
  fn(g, x, y, w, h, extra);
  g.restore();
}
